import React, { useState } from "react";
import AlbumGrid from "@/components/AlbumGrid";
import AlbumCreator from "@/components/AlbumCreator";
import { Album, Image } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Plus, Images, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AddImagesToAlbum from "@/components/AddImagesToAlbum";
import ImageUploader from "@/components/ImageUploader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

interface AlbumsTabProps {
  albums: Album[];
  loading: boolean;
  onDeleteAlbum: (id: string) => Promise<void>;
  onAlbumCreate: (album: Album) => void;
  onImageUpload: (newImage: Image) => void;
}

const AlbumsTab = ({
  albums,
  loading,
  onDeleteAlbum,
  onAlbumCreate,
  onImageUpload,
}: AlbumsTabProps) => {
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null);
  const [isAddImagesOpen, setIsAddImagesOpen] = useState(false);
  const [isAddContentOpen, setIsAddContentOpen] = useState(false);
  const [addContentTab, setAddContentTab] = useState<"existing" | "upload">("existing");

  const handleAddImages = (albumId: string) => {
    const album = albums.find(a => a.id === albumId);
    if (album) {
      setSelectedAlbum(album);
      setIsAddImagesOpen(true);
    }
  };

  const handleAddContent = (albumId: string) => {
    const album = albums.find(a => a.id === albumId);
    if (album) {
      setSelectedAlbum(album);
      setIsAddContentOpen(true);
      setAddContentTab("existing");
    }
  };

  const handleImageUpload = async (newImage: Image) => {
    // Call parent's onImageUpload
    onImageUpload(newImage);
    
    // Add to currently selected album
    if (selectedAlbum) {
      try {
        const { error } = await supabase
          .from('album_images')
          .insert({
            album_id: selectedAlbum.id,
            image_id: newImage.id
          });

        if (error) throw error;
        
        toast.success(`Added "${newImage.name}" to album "${selectedAlbum.name}"`);
        setIsAddContentOpen(false);
      } catch (error) {
        console.error('Error adding image to album:', error);
        toast.error('Failed to add image to album');
      }
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">My Albums</h2>
          <Dialog>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Album
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <h2 className="text-lg font-semibold mb-4">Create New Album</h2>
              <AlbumCreator onSuccess={onAlbumCreate} />
            </DialogContent>
          </Dialog>
        </div>
        <AlbumGrid 
          albums={albums}
          loading={loading}
          onDelete={onDeleteAlbum}
          onAddImages={handleAddContent}
        />
      </Card>

      {/* Dialog for adding content to an album */}
      <Dialog open={isAddContentOpen} onOpenChange={setIsAddContentOpen}>
        <DialogContent className="sm:max-w-[800px]">
          <h2 className="text-lg font-semibold mb-4">
            Add Content to "{selectedAlbum?.name}"
          </h2>

          <Tabs value={addContentTab} onValueChange={(value) => setAddContentTab(value as "existing" | "upload")}>
            <TabsList className="mb-4">
              <TabsTrigger value="existing" className="flex-1">
                <Images className="h-4 w-4 mr-2" />
                Existing Images
              </TabsTrigger>
              <TabsTrigger value="upload" className="flex-1">
                <Upload className="h-4 w-4 mr-2" />
                Upload New
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="existing">
              {selectedAlbum && (
                <AddImagesToAlbum 
                  albumId={selectedAlbum.id} 
                  onSuccess={() => {
                    setIsAddContentOpen(false);
                    toast.success(`Images added to "${selectedAlbum.name}"`);
                  }}
                />
              )}
            </TabsContent>
            
            <TabsContent value="upload">
              {selectedAlbum && (
                <ImageUploader 
                  onUpload={handleImageUpload}
                  onSuccess={() => {
                    // Dialog will be closed by handleImageUpload on success
                  }}
                />
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Legacy dialog - keeping for compatibility */}
      <Dialog open={isAddImagesOpen} onOpenChange={setIsAddImagesOpen}>
        <DialogContent className="sm:max-w-[800px]">
          <h2 className="text-lg font-semibold mb-4">
            Add Images to "{selectedAlbum?.name}"
          </h2>
          {selectedAlbum && (
            <AddImagesToAlbum 
              albumId={selectedAlbum.id} 
              onSuccess={() => {
                setIsAddImagesOpen(false);
                toast.success(`Images added to "${selectedAlbum.name}"`);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AlbumsTab;
